import { createOpenBankingRefund, updateOpenBankingRefund } from "@api";
import {
  AccountNumberInput,
  Button,
  CurrencyInput,
  FormError,
  FormGroup,
  FormLabel,
  Input,
  Modal,
  SortCodeInput,
  TextInput,
} from "@components";
import { toast } from "@components/toast/Toast";
import { formatAmount } from "@utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

// When `refund` is provided the modal edits an existing refund (PUT); otherwise
// it creates a new one (POST). Both prefill from the relevant source.
const IssuePayByBankRefund = ({ payment, refund, isOpen, onClose, onSaved }) => {
  const isEdit = !!refund;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: 0,
      reference: payment?.reference ?? "",
      accountHolderName: payment?.customerName ?? "",
      accountNumber: payment?.bankAccountDetails?.payerAccountNumber ?? "",
      sortCode: payment?.bankAccountDetails?.payerSortCode ?? "",
    },
  });

  // Re-seed the form each time the modal opens (the component stays mounted
  // across openings) — from the refund when editing, otherwise from the payment.
  useEffect(() => {
    if (!isOpen) return;
    if (refund) {
      reset({
        amount: refund.amount,
        reference: refund.reference ?? "",
        accountHolderName: refund.accountHolderName ?? "",
        accountNumber: refund.accountNumber ?? "",
        sortCode: refund.sortCode ?? "",
      });
      setError(null);
    } else if (payment) {
      reset({
        // Deliberately not prefilled with the refundable amount — users were
        // accidentally issuing full refunds by clicking straight through.
        amount: 0,
        reference: payment.reference ?? "",
        accountHolderName: payment.customerName ?? "",
        accountNumber: payment.bankAccountDetails?.payerAccountNumber ?? "",
        sortCode: payment.bankAccountDetails?.payerSortCode ?? "",
      });
      setError(null);
    }
  }, [isOpen, payment, refund]);

  const close = () => {
    setError(null);
    onClose();
  };

  const onSubmit = async (formData) => {
    setSubmitting(true);
    setError(null);

    const response = isEdit
      ? await updateOpenBankingRefund(
          refund.id,
          formData.amount,
          formData.reference,
          formData.sortCode,
          formData.accountNumber,
          formData.accountHolderName,
        )
      : await createOpenBankingRefund(
          payment.id,
          formData.amount,
          formData.reference,
          formData.sortCode,
          formData.accountNumber,
          formData.accountHolderName,
        );

    setSubmitting(false);

    const ok = isEdit ? response.status === 200 : response.status === 201;
    if (!ok) {
      setError(
        "Something went wrong saving the refund. Please check the details and try again.",
      );
      return;
    }

    if (!isEdit) {
      toast({ description: `Created a refund for ${formatAmount(formData.amount)}` });
    }

    onClose();
    if (onSaved) {
      onSaved({ refundId: isEdit ? refund.id : response.data.id, isEdit });
    }
  };

  if (!payment) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={isEdit ? "Edit refund details" : "Issue refund"}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 px-5">
        {!isEdit && (
          <div className="mb-4 rounded-lg bg-blue-50 px-3 py-2.5 text-xs text-blue-800 ring-1 ring-inset ring-blue-200">
            Pay by Bank refunds must be authorised using your company's online
            banking credentials. If you don't have access, set the refund up and
            a finance team member can authorise it later.
          </div>
        )}
        <FormGroup>
          <FormLabel>Amount paid</FormLabel>
          <Input
            prefix="£"
            value={formatAmount(payment.amount, true)}
            readonly={true}
            disabled={true}
          />
        </FormGroup>
        {payment.amountRefunded > 0 && (
          <FormGroup>
            <FormLabel>Previous refunds</FormLabel>
            <Input
              prefix="£"
              value={formatAmount(payment.amountRefunded, true)}
              readonly={true}
              disabled={true}
            />
          </FormGroup>
        )}
        <FormGroup>
          <FormLabel>Refund amount</FormLabel>
          <CurrencyInput
            name="amount"
            control={control}
            displayName="Refund amount"
            required={true}
            max={payment.amount - (payment.amountRefunded ?? 0)}
            validate={(v) =>
              parseFloat(v) > 0 || "Enter a refund amount greater than £0.00"
            }
            errors={errors.amount}
          />
          <FormError name="amount" errors={errors} />
        </FormGroup>
        <FormGroup>
          <FormLabel>Payment reference</FormLabel>
          <TextInput
            name="reference"
            displayName="Reference"
            control={control}
            required={true}
            errors={errors.reference}
          />
          <FormError name="reference" errors={errors} />
        </FormGroup>
        <FormGroup>
          <FormLabel>Account name</FormLabel>
          <TextInput
            name="accountHolderName"
            displayName="Name on account"
            control={control}
            required={true}
            errors={errors.accountHolderName}
          />
          <FormError name="accountHolderName" errors={errors} />
        </FormGroup>
        <FormGroup>
          <FormLabel>Account number</FormLabel>
          <AccountNumberInput
            name="accountNumber"
            displayName="Account number"
            control={control}
            required={true}
            errors={errors.accountNumber}
          />
          <FormError name="accountNumber" errors={errors} />
        </FormGroup>
        <FormGroup>
          <FormLabel>Sort code</FormLabel>
          <SortCodeInput
            name="sortCode"
            displayName="Sort code"
            control={control}
            required={true}
            errors={errors.sortCode}
          />
          <FormError name="sortCode" errors={errors} />
        </FormGroup>
        {error && <p className="text-red-500 text-sm">{error}</p>}
      </form>
      <div className="flex gap-2 justify-end px-5 py-4">
        <Button color="secondary" disabled={submitting} onClick={close}>
          Cancel
        </Button>
        <Button
          color="primary"
          type="submit"
          submitting={submitting}
          onClick={handleSubmit(onSubmit)}
        >
          {isEdit ? "Save" : "Set up refund"}
        </Button>
      </div>
    </Modal>
  );
};

export default IssuePayByBankRefund;
